import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import type {
	AiUsageByFeatureModel,
	AiUsageBySurfaceModel,
} from '../dto/ai-usage.dto';
import { BaseEntity } from './base.entity';
import { decimalNumber } from './decimal-number.transformer';

/**
 * PD-16760: AI credits one user used in one period, rolled up from
 * `ai_usage_event`, plus the limits in force for that user in that period.
 *
 * Written incrementally in the same transaction as each event, and rebuilt
 * nightly from the events for the current and previous period, so a missed
 * or doubled increment never outlives the night. The events stay the truth;
 * this row is what a usage view and (later) the credit check read.
 *
 * Only events attributed to a user land here. System consumers (inbound
 * phone, background AI) are counted in `ai_usage_tenant_period` only.
 *
 * Not audited (`auditable: false`): every metered call rewrites the row, so
 * auditing it would write one `_audit` row per AI call and show nothing the
 * ledger does not already hold.
 *
 * SM1 exposure: none. SM1 names no such table.
 */
@Entity('ai_usage_user_period', {}, { auditable: false })
@Index('uk_ai_usage_user_period_period_user', ['periodKey', 'userId'], {
	unique: true,
})
export class AiUsageUserPeriodEntity extends BaseEntity {
	/** `YYYY-MM`, see `aiUsagePeriodKey`. */
	@Column('char', { name: 'periodKey', length: 7 })
	public periodKey!: string;

	/** Tenant-local `user._id`. */
	@Column('int', { name: 'fk_user__id', unsigned: true })
	public userId!: number;

	/** Global `dvp._user._id` of the same user. */
	@Column('int', { name: 'workdUserId', unsigned: true, nullable: true })
	public workdUserId!: number | null;

	@Column('decimal', {
		name: 'creditsUsed',
		precision: 14,
		scale: 4,
		default: 0,
		transformer: decimalNumber,
	})
	public creditsUsed!: number;

	@Column('int', { name: 'eventCount', unsigned: true, default: 0 })
	public eventCount!: number;

	/**
	 * Limit snapshot for the period: the user's allotment. Null until credit
	 * limits exist (enforcement is a later ticket); null reads as "no limit
	 * recorded", not as zero.
	 */
	@Column('decimal', {
		name: 'allotment',
		precision: 14,
		scale: 4,
		nullable: true,
		transformer: decimalNumber,
	})
	public allotment!: number | null;

	/** Limit snapshot: how far past the allotment the user may draw on the pool. */
	@Column('decimal', {
		name: 'overflowAllowance',
		precision: 14,
		scale: 4,
		nullable: true,
		transformer: decimalNumber,
	})
	public overflowAllowance!: number | null;

	/** Credits used beyond the allotment. Zero while no allotment is recorded. */
	@Column('decimal', {
		name: 'overflowUsed',
		precision: 14,
		scale: 4,
		default: 0,
		transformer: decimalNumber,
	})
	public overflowUsed!: number;

	/** Credits and events by surface. */
	@Column('json', { name: 'usageBySurface', nullable: true })
	public usageBySurface!: AiUsageBySurfaceModel | null;

	/** Credits and events by surface, then feature (the MCP tool name, for example). */
	@Column('json', { name: 'usageByFeature', nullable: true })
	public usageByFeature!: AiUsageByFeatureModel | null;

	@Column('datetime', { name: 'lastActivityAt', precision: 3, nullable: true })
	public lastActivityAt!: Date | null;
}
